<!-- eslint-disable vue/multi-word-component-names -->
<template>
  <ListPageShell>
    <template #header>
    <!-- 页面标题 -->
    <div class="mb-6">
      <h1 class="text-2xl font-bold text-slate-900">加盟商管理</h1>
      <p class="text-slate-600 mt-1">管理加盟商信息，包括资质审核、区域管理、业绩考核等</p>
    </div>
    </template>

    <!-- 统计卡片 -->
    <div class="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
      <div class="group bg-white rounded-xl border border-slate-200 p-5 hover:shadow-lg hover:border-primary-200 transition-all duration-300 cursor-pointer">
        <div class="flex items-center justify-between mb-3">
          <div class="w-12 h-12 bg-gradient-to-br from-primary-100 to-primary-50 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
            <svg class="w-6 h-6 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"/>
            </svg>
          </div>
          <span class="status-badge bg-primary-50 text-primary-600">总计</span>
        </div>
        <div class="text-3xl font-bold text-slate-900 mb-1">{{ stats.total }}</div>
        <div class="text-sm text-slate-500">全部加盟商</div>
      </div>
      <div class="group bg-white rounded-xl border border-slate-200 p-5 hover:shadow-lg hover:border-green-200 transition-all duration-300 cursor-pointer">
        <div class="flex items-center justify-between mb-3">
          <div class="w-12 h-12 bg-gradient-to-br from-green-100 to-green-50 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
            <svg class="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
            </svg>
          </div>
          <span class="status-badge bg-green-50 text-green-600">活跃</span>
        </div>
        <div class="text-3xl font-bold text-green-600 mb-1">{{ stats.active }}</div>
        <div class="text-sm text-slate-500">正式加盟商</div>
      </div>
      <div class="group bg-white rounded-xl border border-slate-200 p-5 hover:shadow-lg hover:border-amber-200 transition-all duration-300 cursor-pointer">
        <div class="flex items-center justify-between mb-3">
          <div class="w-12 h-12 bg-gradient-to-br from-amber-100 to-amber-50 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
            <svg class="w-6 h-6 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/>
            </svg>
          </div>
          <span class="status-badge bg-amber-50 text-amber-600">待处理</span>
        </div>
        <div class="text-3xl font-bold text-amber-600 mb-1">{{ stats.pending }}</div>
        <div class="text-sm text-slate-500">待审核加盟商</div>
      </div>
      <div class="group bg-white rounded-xl border border-slate-200 p-5 hover:shadow-lg hover:border-red-200 transition-all duration-300 cursor-pointer">
        <div class="flex items-center justify-between mb-3">
          <div class="w-12 h-12 bg-gradient-to-br from-red-100 to-red-50 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
            <svg class="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z"/>
            </svg>
          </div>
          <span class="status-badge bg-red-50 text-red-600">非活跃</span>
        </div>
        <div class="text-3xl font-bold text-red-600 mb-1">{{ stats.suspended }}</div>
        <div class="text-sm text-slate-500">已暂停加盟商</div>
      </div>
    </div>

    <!-- 搜索和筛选 -->
    <div class="bg-white rounded-xl border border-slate-200 mb-6 overflow-hidden">
      <PageFilterBar
        body-class="p-4 bg-slate-50 border-b border-slate-100"
        :show-refresh="true"
        :refresh-loading="loading"
        @refresh="fetchFranchisees"
      >
          <div class="relative flex-1 min-w-[200px] max-w-xs">
            <svg class="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/>
            </svg>
            <input
              v-model="searchForm.name"
              type="text"
              placeholder="搜索加盟商名称、编号..."
              class="w-full pl-10 pr-4 py-2.5 bg-white border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all"
              @keyup.enter="handleSearch"
            >
          </div>

          <div class="w-[140px]">
            <el-select
              v-model="searchForm.statuses"
              multiple
              collapse-tags
              placeholder="状态"
              clearable
              class="w-full"
            >
              <el-option
                v-for="s in statusOptions"
                :key="s.value"
                :label="s.label"
                :value="s.value"
              />
            </el-select>
          </div>

          <div class="w-[120px]">
            <el-select
              v-model="searchForm.levels"
              multiple
              collapse-tags
              placeholder="等级"
              clearable
              class="w-full"
            >
              <el-option :value="0" label="普通" />
              <el-option :value="1" label="银牌" />
              <el-option :value="2" label="金牌" />
              <el-option :value="3" label="钻石" />
            </el-select>
          </div>

          <div class="w-[100px]">
            <el-select
              v-model="searchForm.majorRegion"
              placeholder="区域"
              clearable
              class="w-full"
            >
              <el-option
                v-for="region in regions"
                :key="region"
                :label="region"
                :value="region"
              />
            </el-select>
          </div>

        <template #searchActions>
          <button
            @click="handleSearch"
            class="btn-primary inline-flex items-center gap-2"
          >
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/>
            </svg>
            搜索
          </button>
          <button
            @click="handleReset"
            class="btn-secondary inline-flex items-center gap-2"
          >
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"/>
            </svg>
            重置
          </button>
        </template>
      </PageFilterBar>

      <!-- 操作栏 -->
      <div class="px-5 py-3.5 bg-white flex justify-between items-center">
        <div class="flex items-center gap-2">
          <span class="text-sm text-slate-500">共</span>
          <span class="text-sm font-semibold text-slate-900">{{ pagination.total }}</span>
          <span class="text-sm text-slate-500">条加盟商</span>
        </div>
        <div class="flex items-center gap-2">
          <button
            v-permission="'franchisee:add'"
            @click="openCreateModal"
            class="flex items-center gap-2 px-4 py-2.5 bg-gradient-to-r from-primary-600 to-primary-500 text-white rounded-lg hover:from-primary-700 hover:to-primary-600 shadow-lg shadow-primary-500/25 hover:shadow-xl hover:shadow-primary-500/30 transition-all duration-200 font-medium"
          >
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"/>
            </svg>
            新建加盟商
          </button>
        </div>
      </div>

      <!-- 加盟商表格 -->
      <div class="list-page-table-panel">
        <div class="data-table-shell">
          <div class="data-table-scroll">
            <table class="data-table text-sm">
              <thead class="data-table-head">
                <tr>
                  <th class="data-table-th text-left">加盟商信息</th>
                  <th class="data-table-th text-left">经营区域</th>
                  <th class="data-table-th text-left">负责人</th>
                  <th class="data-table-th text-left">等级</th>
                  <th class="data-table-th text-right">订单数</th>
                  <th class="data-table-th text-right">业绩金额</th>
                  <th class="data-table-th text-center">状态</th>
                  <th class="data-table-th text-center">操作</th>
                </tr>
              </thead>
              <tbody>
                <!-- 骨架屏加载状态 -->
                <template v-if="loading">
                  <tr v-for="n in 5" :key="n" class="animate-pulse">
                  <td class="data-table-td">
                    <div class="flex items-center gap-3">
                      <div class="w-11 h-11 bg-slate-200 rounded-xl"></div>
                      <div>
                        <div class="h-4 w-32 bg-slate-200 rounded mb-1"></div>
                        <div class="h-3 w-24 bg-slate-200 rounded"></div>
                      </div>
                    </div>
                  </td>
                  <td class="data-table-td"><div class="h-4 w-20 bg-slate-200 rounded"></div></td>
                  <td class="data-table-td"><div class="h-4 w-16 bg-slate-200 rounded"></div></td>
                  <td class="data-table-td is-center"><div class="h-5 w-12 bg-slate-200 rounded-full"></div></td>
                  <td class="data-table-td is-right"><div class="h-4 w-12 bg-slate-200 rounded ml-auto"></div></td>
                  <td class="data-table-td is-right"><div class="h-4 w-20 bg-slate-200 rounded ml-auto"></div></td>
                  <td class="data-table-td is-center"><div class="h-6 w-14 bg-slate-200 rounded-full mx-auto"></div></td>
                  <td class="data-table-td is-center">
                    <div class="flex items-center justify-center gap-1">
                      <div class="h-8 w-8 bg-slate-200 rounded"></div>
                      <div class="h-8 w-8 bg-slate-200 rounded"></div>
                      <div class="h-8 w-8 bg-slate-200 rounded"></div>
                    </div>
                  </td>
                </tr>
                </template>
                <!-- 空状态 -->
                <tr v-if="!loading && franchisees.length === 0">
                  <td colspan="8" class="data-table-empty">暂无加盟商数据</td>
                </tr>
                <tr v-for="franchisee in franchisees" :key="franchisee.id" class="data-table-row">
                  <td class="data-table-td">
                    <div class="flex items-center gap-3">
                      <div class="w-11 h-11 bg-gradient-to-br from-primary-100 to-primary-50 rounded-xl flex items-center justify-center">
                        <svg class="w-5 h-5 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"/>
                        </svg>
                      </div>
                      <div>
                        <div class="font-semibold text-slate-900">{{ franchisee.name }}</div>
                        <div class="text-xs text-slate-400 flex items-center gap-2">
                          <span>{{ franchisee.franchiseeNo || '-' }}</span>
                          <span v-if="franchisee.joinDate" class="text-slate-300">|</span>
                          <span v-if="franchisee.joinDate">加盟: {{ franchisee.joinDate }}</span>
                        </div>
                      </div>
                    </div>
                  </td>
                  <td class="data-table-td">
                    <div class="flex flex-col">
                      <span class="text-sm font-medium text-slate-700">{{ franchisee.majorRegion || '-' }}</span>
                      <span v-if="franchisee.minorRegion" class="text-xs text-slate-400">{{ franchisee.minorRegion }}</span>
                    </div>
                  </td>
                  <td class="data-table-td">
                    <div class="flex flex-col">
                      <span class="text-sm font-medium text-slate-700">{{ franchisee.leader || '-' }}</span>
                      <span v-if="franchisee.phone" class="text-xs text-slate-400">{{ franchisee.phone }}</span>
                    </div>
                  </td>
                  <td class="data-table-td">
                    <span :class="['status-badge', getLevelClass(franchisee.level)]">
                      {{ franchisee.levelDesc || getLevelText(franchisee.level) }}
                    </span>
                  </td>
                  <td class="data-table-td is-right font-semibold text-slate-700">{{ franchisee.orderCount || 0 }}</td>
                  <td class="data-table-td is-right font-bold text-slate-900">¥{{ (franchisee.performanceAmount || 0).toLocaleString() }}</td>
                  <td class="data-table-td is-center">
                    <span :class="['px-3 py-1.5 rounded-full text-xs font-semibold', getStatusClass(franchisee.status)]">
                      {{ franchisee.statusDesc || getStatusText(franchisee.status) }}
                    </span>
                  </td>
                  <td class="data-table-td is-center">
                    <div class="flex items-center justify-center gap-1">
                      <button @click="openDetailModal(franchisee)" class="table-action-button is-primary" title="查看详情">
                        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/>
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"/>
                        </svg>
                      </button>
                      <button v-permission="'franchisee:account:query'" @click="openAccountDetailModal(franchisee)" class="table-action-button is-teal" title="分账账户">
                        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"/>
                        </svg>
                      </button>
                      <button v-permission="'franchisee:edit'" @click="openEditModal(franchisee)" class="table-action-button is-primary" title="编辑">
                        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"/>
                        </svg>
                      </button>
                      <button v-if="franchisee.status === 2" v-permission="'franchisee:edit'" @click="handleApprove(franchisee)" class="table-action-button is-teal" title="审核通过">
                        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
                        </svg>
                      </button>
                      <button v-permission="'franchisee:delete'" @click="handleDelete(franchisee)" class="table-action-button is-danger" title="删除">
                        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/>
                        </svg>
                      </button>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

    </div>

      <!-- 空状态 -->
      <div v-if="!loading && franchisees.length === 0" class="py-16 text-center">
        <div class="w-20 h-20 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-5">
          <svg class="w-10 h-10 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"/>
          </svg>
        </div>
        <h3 class="text-lg font-semibold text-slate-700 mb-2">暂无加盟商数据</h3>
        <p class="text-slate-500 mb-6">点击下方按钮开始添加第一个加盟商吧</p>
        <button
          @click="openCreateModal"
          class="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-primary-600 to-primary-500 text-white rounded-lg hover:from-primary-700 hover:to-primary-600 shadow-lg shadow-primary-500/25 mx-auto transition-all duration-200 font-medium"
        >
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"/>
          </svg>
          新建加盟商
        </button>
      </div>

      <!-- 分页 -->
      <AppPagination
        v-if="pagination.pages > 1"
        class="pagination-bridge"
        :pagination="pagination"
        :page-sizes="[10, 20, 50]"
        @page-change="handlePageChange"
        @size-change="handlePageSizeChange"
      />
    </div>

    <!-- 新建/编辑加盟商弹窗 -->
    <Transition name="modal-fade">
      <div v-if="showFormModal" class="fixed inset-0 z-50 overflow-y-auto">
        <div class="fixed inset-0 bg-black/50 backdrop-blur-sm" @click="closeFormModal"></div>
        <div class="relative min-h-screen flex items-center justify-center p-4">
          <div class="relative bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden transform transition-all">
            <!-- 头部 -->
            <div class="flex items-center justify-between px-6 py-4 bg-gradient-to-r from-slate-50 to-white border-b border-slate-100">
              <div class="flex items-center gap-3">
                <div class="w-10 h-10 bg-gradient-to-br from-primary-100 to-primary-50 rounded-xl flex items-center justify-center">
                  <svg class="w-5 h-5 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"/>
                  </svg>
                </div>
                <div>
                  <h3 class="text-lg font-semibold text-slate-900">{{ isEdit ? '编辑加盟商' : '新建加盟商' }}</h3>
                  <p class="text-xs text-slate-500">{{ isEdit ? '修改加盟商信息' : '填写加盟商基本信息' }}</p>
                </div>
              </div>
              <button @click="closeFormModal" class="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-all duration-200">
                <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
                </svg>
              </button>
            </div>

            <div class="overflow-y-auto max-h-[calc(90vh-140px)]">
              <div class="grid grid-cols-1 md:grid-cols-2 gap-5 p-6">
                <div class="group">
                  <label class="block text-sm font-medium text-slate-700 mb-2">
                    加盟商名称 <span class="text-red-500">*</span>
                  </label>
                  <input v-model="formData.name" type="text" class="form-input" placeholder="请输入加盟商名称">
                </div>
                <div class="group">
                  <label class="block text-sm font-medium text-slate-700 mb-2">
                    大区域 <span class="text-red-500">*</span>
                  </label>
                  <select v-model="formData.majorRegion" class="form-select-native">
                    <option value="">请选择区域</option>
                    <option v-for="region in regions" :key="region" :value="region">{{ region }}</option>
                  </select>
                </div>
                <div class="group">
                  <label class="block text-sm font-medium text-slate-700 mb-2">省市区</label>
                  <el-cascader
                    ref="areaCascaderRef"
                    v-model="areaCodes"
                    :options="regionTree"
                    placeholder="请选择省市区"
                    filterable
                    clearable
                    class="w-full"
                    @change="handleAreaChange"
                  />
                </div>
                <div class="group">
                  <label class="block text-sm font-medium text-slate-700 mb-2">详细地址</label>
                  <input v-model="formData.address" type="text" class="form-input" placeholder="请输入详细地址">
                </div>
                <div class="group">
                  <label class="block text-sm font-medium text-slate-700 mb-2">加盟等级</label>
                  <select v-model="formData.level" class="form-select-native">
                    <option :value="0">普通加盟商</option>
                    <option :value="1">银牌加盟商</option>
                    <option :value="2">金牌加盟商</option>
                    <option :value="3">钻石加盟商</option>
                  </select>
                </div>
                <div class="group">
                  <label class="block text-sm font-medium text-slate-700 mb-2">负责人</label>
                  <input v-model="formData.leader" type="text" class="form-input" placeholder="请输入负责人姓名">
                </div>
                <div class="group">
                  <label class="block text-sm font-medium text-slate-700 mb-2">联系方式</label>
                  <input v-model="formData.phone" type="text" class="form-input" placeholder="请输入联系方式">
                </div>
                <div class="group">
                  <label class="block text-sm font-medium text-slate-700 mb-2">加盟日期</label>
                  <el-date-picker v-model="formData.joinDate" type="date" class="form-date" format="YYYY年MM月DD日" value-format="YYYY-MM-DD" placeholder="请选择加盟日期" />
                </div>
                <div class="group">
                  <label class="block text-sm font-medium text-slate-700 mb-2">到期日期</label>
                  <el-date-picker v-model="formData.expireDate" type="date" class="form-date" format="YYYY年MM月DD日" value-format="YYYY-MM-DD" placeholder="请选择到期日期" />
                </div>
                <div class="group">
                  <label class="block text-sm font-medium text-slate-700 mb-2">保证金 (¥)</label>
                  <input v-model.number="formData.deposit" type="number" class="form-input" placeholder="请输入保证金金额">
                </div>
                <div class="group">
                  <label class="block text-sm font-medium text-slate-700 mb-2">状态</label>
                  <select v-model="formData.status" class="form-select-native">
                    <option :value="0">潜在加盟商</option>
                    <option :value="1">意向加盟商</option>
                    <option :value="2">签约审核中</option>
                    <option :value="3">正式加盟商</option>
                    <option :value="4">暂停</option>
                    <option :value="5">终止</option>
                  </select>
                </div>
                <div class="md:col-span-2 group">
                  <label class="block text-sm font-medium text-slate-700 mb-2">备注信息</label>
                  <textarea v-model="formData.remark" rows="3" class="form-textarea" placeholder="请输入备注信息"></textarea>
                </div>

                <!-- 产品授权 -->
                <div class="md:col-span-2 group">
                  <label class="block text-sm font-medium text-slate-700 mb-2">
                    授权产品
                    <span class="text-xs font-normal text-slate-400 ml-2">选择授权给该加盟商的产品（留空则授权全部产品）</span>
                  </label>
                  <div class="border border-slate-200 rounded-lg p-4 bg-slate-50">
                    <div class="flex items-center justify-between mb-3">
                      <span class="text-sm text-slate-600">已选择 <span class="font-semibold text-primary-600">{{ authorizedProducts.length }}</span> 个产品</span>
                      <button
                        @click="openProductSelector"
                        type="button"
                        class="text-sm text-primary-600 hover:text-primary-700 font-medium"
                      >
                        选择产品
                      </button>
                    </div>
                    <div v-if="authorizedProducts.length > 0" class="flex flex-wrap gap-2">
                      <span class="text-xs text-slate-500 px-2 py-1 bg-white border border-slate-200 rounded">
                        {{ authorizedProducts.length }} 个产品已授权
                      </span>
                    </div>
                    <div v-else class="text-sm text-slate-400">
                      暂未选择产品，将授权全部产品给该加盟商
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class="px-6 py-4 bg-slate-50 border-t border-slate-100 flex justify-end gap-3">
              <button @click="closeFormModal" class="px-5 py-2.5 border border-slate-200 text-slate-700 rounded-lg hover:bg-white hover:shadow-sm hover:border-slate-300 transition-all duration-200 font-medium">
                取消
              </button>
              <button @click="handleSubmit" :loading="submitting" class="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-primary-600 to-primary-500 text-white rounded-lg hover:from-primary-700 hover:to-primary-600 shadow-lg shadow-primary-500/25 hover:shadow-xl hover:shadow-primary-500/30 transition-all duration-200 font-medium">
                <svg v-if="submitting" class="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                  <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                  <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                <svg v-else class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/>
                </svg>
                {{ isEdit ? '保存修改' : '创建加盟商' }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </Transition>

    <!-- 加盟商详情弹窗 -->
    <FranchiseeDetailModal
      v-model:visible="showDetailModal"
      :franchisee-id="selectedFranchisee?.id"
      :franchisee-info="selectedFranchisee"
      :initial-tab="detailInitialTab"
      @open-platform-receiver="openPlatformReceiverFromDetail"
    />

    <!-- 平台分账接收方账户弹窗 -->
    <PlatformLakalaReceiverFormModal
      v-model:visible="showPlatformReceiverModal"
      :franchisee-id="selectedFranchisee?.id"
      :franchisee-info="selectedFranchisee"
      @success="handlePlatformReceiverSuccess"
    />

    <!-- 产品选择器弹窗 -->
    <ProductSelector
      v-model:visible="showProductSelector"
      v-model="authorizedProducts"
      :product-tree="productTree"
      title="选择授权产品"
      description="选择要授权给该加盟商的产品"
    />

  </ListPageShell>
</template>

<script setup>
import { ref, reactive, computed, onMounted } from 'vue'
import { ElMessage, ElMessageBox, ElSelect, ElOption, ElCascader } from 'element-plus'
import {
  buildChinaAreaOptions,
  chinaAreaData,
  closeChinaAreaCascaderAfterDistrict,
  codeTextMap,
  provinceData
} from '@/utils/chinaAreaOptions.mjs'
const CodeToText = codeTextMap
import {
  getFranchiseePage,
  getFranchisee,
  createFranchisee,
  updateFranchisee,
  deleteFranchisee,
  changeFranchiseeStatus
} from '@/api'
import { getProductTree } from '@/api/product'
import FranchiseeDetailModal from './components/FranchiseeDetailModal.vue'
import PlatformLakalaReceiverFormModal from './components/PlatformLakalaReceiverFormModal.vue'
import ProductSelector from './components/ProductSelector.vue'
import AppPagination from '@/components/AppPagination.vue'
import ListPageShell from '@/components/ListPageShell.vue'
import PageFilterBar from '@/components/PageFilterBar.vue'

// 地区配置
const regions = ['华东', '华北', '华南', '华中', '西南', '西北', '东北']

// 状态选项
const statusOptions = [
  { value: 0, label: '潜在加盟商' },
  { value: 1, label: '意向加盟商' },
  { value: 2, label: '签约审核中' },
  { value: 3, label: '正式加盟商' },
  { value: 4, label: '暂停' },
  { value: 5, label: '终止' }
]

// 将扁平的地区数据转换为树形结构（用于级联选择器）
const regionTree = computed(() => buildChinaAreaOptions())

// 加载状态
const loading = ref(false)
const submitting = ref(false)

// 加盟商列表
const franchisees = ref([])
const selectedFranchisee = ref(null)

// 统计
const stats = reactive({
  total: 0,
  active: 0,
  pending: 0,
  suspended: 0
})

// 搜索表单
const searchForm = reactive({
  name: '',
  statuses: [],
  levels: [],
  majorRegion: '',
  needPerformance: true
})

// 分页
const pagination = reactive({
  current: 1,
  size: 10,
  total: 0,
  pages: 0,
  hasNext: false,
  hasPrevious: false
})

// 表单弹窗
const showFormModal = ref(false)
const isEdit = ref(false)
const formData = reactive({
  id: null,
  name: '',
  status: 0,
  level: 0,
  leader: '',        // 负责人
  phone: '',         // 联系方式
  majorRegion: '',
  minorRegion: '',   // 省市区字符串，格式：省/市/区
  address: '',
  joinDate: '',
  expireDate: '',
  deposit: 0,
  remark: ''
})

// 详情弹窗
const showDetailModal = ref(false)
const detailInitialTab = ref('basic')
const showPlatformReceiverModal = ref(false)

// 产品授权相关
const authorizedProducts = ref([])  // 选中的授权产品ID列表
const showProductSelector = ref(false)  // 是否显示产品选择器
const productTree = ref([])  // 产品树数据
const productTreeLoading = ref(false)  // 产品树加载状态

// 状态文本映射
const statusTextMap = {
  0: '潜在加盟商',
  1: '意向加盟商',
  2: '签约审核中',
  3: '正式加盟商',
  4: '暂停',
  5: '终止'
}

// 等级文本映射
const levelTextMap = {
  0: '普通加盟商',
  1: '银牌加盟商',
  2: '金牌加盟商',
  3: '钻石加盟商'
}

// 获取状态文本
const getStatusText = (status) => {
  return statusTextMap[status] || '未知'
}

// 获取等级文本
const getLevelText = (level) => {
  return levelTextMap[level] || '未知'
}

// 获取状态样式
const getStatusClass = (status) => {
  const classes = {
    0: 'bg-slate-100 text-slate-700',
    1: 'bg-blue-100 text-blue-700',
    2: 'bg-amber-100 text-amber-700',
    3: 'bg-green-100 text-green-700',
    4: 'bg-red-100 text-red-700',
    5: 'bg-slate-100 text-slate-600'
  }
  return classes[status] || 'bg-slate-100 text-slate-600'
}

// 获取等级样式
const getLevelClass = (level) => {
  const classes = {
    0: 'bg-slate-100 text-slate-600',
    1: 'bg-slate-200 text-slate-700',
    2: 'bg-amber-100 text-amber-700',
    3: 'bg-purple-100 text-purple-700'
  }
  return classes[level] || 'bg-slate-100 text-slate-600'
}

// 加载加盟商列表
const fetchFranchisees = async () => {
  loading.value = true
  try {
    const params = {
      name: searchForm.name || undefined,
      statuses: searchForm.statuses?.length ? searchForm.statuses : undefined,
      levels: searchForm.levels?.length ? searchForm.levels : undefined,
      majorRegion: searchForm.majorRegion || undefined,
      needPerformance: true,
      current: pagination.current,
      size: pagination.size
    }
    const res = await getFranchiseePage(params)
    franchisees.value = res.records || []
    pagination.total = res.total || 0
    pagination.pages = res.pages || 0
    pagination.hasNext = res.hasNext || false
    pagination.hasPrevious = res.hasPrevious || false

    // 更新统计
    stats.total = res.total || 0
    // 暂时使用模拟数据更新统计
    stats.active = franchisees.value.filter(f => f.status === 3).length
    stats.pending = franchisees.value.filter(f => f.status === 2).length
    stats.suspended = franchisees.value.filter(f => f.status === 4).length
  } catch (error) {
    console.error('获取加盟商列表失败:', error)
    ElMessage.error('获取加盟商列表失败')
  } finally {
    loading.value = false
  }
}

// 搜索
const handleSearch = () => {
  pagination.current = 1
  fetchFranchisees()
}

// 重置
const handleReset = () => {
  searchForm.name = ''
  searchForm.statuses = []
  searchForm.levels = []
  searchForm.majorRegion = ''
  pagination.current = 1
  fetchFranchisees()
}

// 分页切换
const handlePageChange = (page) => {
  if (page < 1 || page > pagination.pages) return
  pagination.current = page
  fetchFranchisees()
}

const handlePageSizeChange = (size) => {
  pagination.size = size
  pagination.current = 1
  fetchFranchisees()
}

// 加载产品树
const loadProductTree = async () => {
  productTreeLoading.value = true
  try {
    const res = await getProductTree(0)
    productTree.value = res || []
  } catch (error) {
    console.error('加载产品树失败:', error)
    productTree.value = []
  } finally {
    productTreeLoading.value = false
  }
}

// 打开产品选择器
const openProductSelector = async () => {
  if (productTree.value.length === 0) {
    await loadProductTree()
  }
  showProductSelector.value = true
}

// 打开新建弹窗
const openCreateModal = () => {
  isEdit.value = false
  Object.assign(formData, {
    id: null,
    name: '',
    status: 0,
    level: 0,
    leader: '',
    phone: '',
    majorRegion: '',
    minorRegion: '',
    address: '',
    joinDate: '',
    expireDate: '',
    deposit: 0,
    remark: ''
  })
  areaCodes.value = []
  authorizedProducts.value = []  // 重置产品选择
  showFormModal.value = true
}

// 打开编辑弹窗
const openEditModal = async (franchisee) => {
  isEdit.value = true
  try {
    const res = await getFranchisee(franchisee.id)
    // 从 minorRegion 解析，设置 areaCodes
    if (res.minorRegion) {
      const parts = res.minorRegion.split('/')
      // 查找对应的code
      const findCode = (name, parentCode = '86') => {
        const parentData = parentCode === '86' ? provinceData : chinaAreaData[parentCode]
        for (const [code, n] of Object.entries(parentData || {})) {
          if (n === name) return code
        }
        return null
      }
      const provinceCode = findCode(parts[0])
      const cityCode = provinceCode ? findCode(parts[1], provinceCode) : null
      const districtCode = cityCode ? findCode(parts[2], cityCode) : null
      areaCodes.value = [provinceCode, cityCode, districtCode].filter(Boolean)
    } else {
      areaCodes.value = []
    }
    Object.assign(formData, {
      id: res.id,
      name: res.name || '',
      status: res.status ?? 0,
      level: res.level ?? 0,
      leader: res.leader || '',
      phone: res.phone || '',
      majorRegion: res.majorRegion || '',
      minorRegion: res.minorRegion || '',
      address: res.address || '',
      joinDate: res.joinDate || '',
      expireDate: res.expireDate || '',
      deposit: res.deposit || 0,
      remark: res.remark || ''
    })
    showFormModal.value = true
  } catch (error) {
    console.error('获取加盟商详情失败:', error)
    ElMessage.error('获取加盟商详情失败')
  }
}

// 关闭表单弹窗
const closeFormModal = () => {
  showFormModal.value = false
}

// 省市区选择变化
const areaCodes = ref([])  // 临时存储选择的code
const areaCascaderRef = ref(null)
const handleAreaChange = (value) => {
  if (value && value.length >= 3) {
    const province = provinceData[value[0]] || ''  // 省份从 provinceData 获取
    const city = CodeToText[value[1]] || ''        // 城市从 CodeToText 获取
    const district = CodeToText[value[2]] || ''    // 区县从 CodeToText 获取
    formData.minorRegion = `${province}/${city}/${district}`
    closeChinaAreaCascaderAfterDistrict(areaCascaderRef.value, value)
  } else {
    formData.minorRegion = ''
    areaCodes.value = []
  }
}

// 提交表单
const handleSubmit = async () => {
  if (!formData.name.trim()) {
    ElMessage.warning('请输入加盟商名称')
    return
  }
  if (!formData.majorRegion) {
    ElMessage.warning('请选择大区域')
    return
  }

  submitting.value = true
  try {
    // 构建提交数据，添加授权产品ID列表
    const submitData = {
      ...formData,
      // 新建时传递授权产品ID列表，为空则初始化全部产品
      productIds: authorizedProducts.value.length > 0 ? authorizedProducts.value : undefined
    }

    if (isEdit.value) {
      await updateFranchisee(submitData)
      ElMessage.success('更新成功')
    } else {
      await createFranchisee(submitData)
      ElMessage.success('创建成功')
    }
    closeFormModal()
    fetchFranchisees()
  } catch (error) {
    console.error('保存失败:', error)
    ElMessage.error(error.message || '保存失败')
  } finally {
    submitting.value = false
  }
}

// 删除加盟商
const handleDelete = async (franchisee) => {
  try {
    await ElMessageBox.confirm(
      `确定要删除加盟商 "${franchisee.name}" 吗？此操作不可恢复。`,
      '删除确认',
      { type: 'warning' }
    )
    await deleteFranchisee(franchisee.id)
    ElMessage.success('删除成功')
    fetchFranchisees()
  } catch (error) {
    if (error !== 'cancel') {
      console.error('删除失败:', error)
      ElMessage.error('删除失败')
    }
  }
}

// 审核通过
const handleApprove = async (franchisee) => {
  try {
    await ElMessageBox.confirm(
      `确定要通过加盟商 "${franchisee.name}" 的审核吗？`,
      '审核确认',
      { type: 'info' }
    )
    await changeFranchiseeStatus({
      id: franchisee.id,
      toStatus: 3, // 正式加盟商
      reason: '审核通过'
    })
    ElMessage.success('审核已通过')
    fetchFranchisees()
  } catch (error) {
    if (error !== 'cancel') {
      console.error('审核失败:', error)
      ElMessage.error('审核失败')
    }
  }
}

// 打开详情弹窗
const openDetailModal = (franchisee, initialTab = 'basic') => {
  selectedFranchisee.value = franchisee
  detailInitialTab.value = initialTab
  showDetailModal.value = true
}

const openAccountDetailModal = (franchisee) => {
  openDetailModal(franchisee, 'bank')
}

const openPlatformReceiverFromDetail = () => {
  if (!selectedFranchisee.value) return
  showPlatformReceiverModal.value = true
}

const handlePlatformReceiverSuccess = () => {
  fetchFranchisees()
  detailInitialTab.value = 'bank'
}

// 初始化
onMounted(() => {
  fetchFranchisees()
})
</script>

<style scoped>
/* 弹窗过渡动画 */
.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: all 0.3s ease;
}

.modal-fade-enter-from,
.modal-fade-leave-to {
  opacity: 0;
}

.modal-fade-enter-from .relative,
.modal-fade-leave-to .relative {
  transform: scale(0.95) translateY(-10px);
}

.modal-fade-enter-active .relative,
.modal-fade-leave-active .relative {
  transition: all 0.3s ease;
}
</style>
